import { FC, useEffect, useReducer, useState, createContext, useContext, ReactNode } from 'react';
import PoiCityRaceView from '@utmb/types/view/PoiCityRaceView';

import RaceTrackPolylineView from '@utmb/types/view/RaceTrackPolylineView';
import { CityMapsContextProps, CityMapsFilters } from '@utmb/types/city-maps';
import { ActionKind, initialState, reducer, SetLegendCityAction, SetPoisAction, SetSelectedCityAction, SetSelectedRaceAction } from '@utmb/reducers/city-maps';
import { useTenancy } from '@utmb/contexts/TenancyContext';
import { fetchClientAPI } from '@utmb/utils/utmb';

const CityMapsContext = createContext({} as CityMapsContextProps);

interface CityMapsProviderProps {
    pois: PoiCityRaceView[];
    children?: ReactNode;
}

export const CityMapsProvider: FC<CityMapsProviderProps> = (props) => {
    const { pois, children } = props;

    const { tenantId } = useTenancy();

    const [state, dispatch] = useReducer(reducer, initialState);
    const [polylines, setPolylines] = useState<RaceTrackPolylineView[]>([]);

    const fetchPolyline = async (raceId: number): Promise<void> => {
        const request = await fetchClientAPI(tenantId, `/content/race-tracks/polyline/${raceId}`);

        const polyline: RaceTrackPolylineView = request;

        setPolylines([...polylines, polyline]);
    };

    const setSelectedRaceId = (raceId: number): void => {
        dispatch({ type: ActionKind.SELECT_RACE, payload: raceId } as SetSelectedRaceAction);

        // Fetch race polyline if needed
        if (state.legend.polyline && !polylines.find(({ raceId: id }) => raceId === id)) {
            fetchPolyline(raceId).then();
        }
    };

    const setSelectedCity = (city: string): void => {
        dispatch({ type: ActionKind.SELECT_CITY, payload: city } as SetSelectedCityAction);
    };

    const setLegend = (legend: CityMapsFilters): void => {
        dispatch({ type: ActionKind.SET_LEGEND, payload: legend } as SetLegendCityAction);

        if (legend.polyline) {
            fetchPolyline(state.selectedRaceId!).then();
        }
    };

    useEffect(() => {
        dispatch({ type: ActionKind.SET_POIS, payload: pois } as SetPoisAction);
    }, [pois]);

    return (
        <CityMapsContext.Provider
            value={{
                ...state,
                polylines,
                setSelectedRaceId,
                setSelectedCity,
                setLegend,
            }}
        >
            {children}
        </CityMapsContext.Provider>
    );
};

export const useCityMaps = () => {
    const context = useContext(CityMapsContext);
    if (context === undefined) {
        throw new Error(`useCityMaps must be used within an CityMapsProvider`);
    }
    return context;
};
